import { useMutation, useQuery } from '@apollo/client'
import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import Toggle from 'react-toggle'

import { Card } from '../../../../components'
import { DatePicker } from '../../../../components/date-picker'
import { Modal } from '../../../../components/modal'
import { ToggleIcon } from '../../../../components/security-and-login/icons'
import { SubMenu } from '../../../../components/sub-menu'
import { UserItemProps } from '../../../../components/user-item/user-item'
import { Notify } from '../../../../lib/helpers'
import {
    DeleteUserData,
    GetPreQualificationData,
    RefetchUserData,
} from './queries'

interface UserProps {
    user: UserItemProps
}

enum DeleteTypes {
    SOFT = 'SOFT',
    HARD = 'HARD',
    TOTAL = 'TOTAL',
}

export function User({ user }: UserProps) {
    const history = useHistory()
    const location = useLocation()
    const { role, firstName, lastName, email, lastLoginDate, phone } =
        user ?? {}
    const [open, setOpen] = useState(false)
    const [regenerationDates, setRegenerationDates] = useState([
        new Date(),
        new Date(),
    ])
    const [refetchUserData] = useMutation(RefetchUserData, {
        onError: error => {
            Notify('danger', 'Whoopsies', error.message)
        },
        onCompleted: data => {
            Notify(
                'success',
                'Yippee!',
                `The refetch of the users data has been triggered`,
            )
        },
    })

    const { data: preQualificationStats } = useQuery(GetPreQualificationData, {
        variables: { userId: user?.id || '' },
    })

    const [deleteUserData] = useMutation(DeleteUserData, {
        onError: error => {
            Notify('danger', 'Whoopsies', error.message)
        },
        onCompleted: data => {
            Notify('success', 'Yippee!', `Deleted users data`)
        },
    })

    const triggerRefetchUserData = async () => {
        await refetchUserData({
            variables: {
                input: {
                    userId: user.id,
                    from: regenerationDates[0],
                    to: regenerationDates[1],
                },
            },
        })
    }

    const triggerDeleteUserData = async (deleteType: DeleteTypes) => {
        await deleteUserData({
            variables: {
                userId: user.id,
                deleteType,
            },
        })
    }

    const lastLogin = useMemo(() => {
        if (lastLoginDate) {
            const date = new Date(lastLoginDate)
            return date.toLocaleString()
        }

        return 'No last login'
    }, [lastLoginDate])

    const modalStyles = classNames('h-screen w-screen fixed top-0 left-0')

    const handlePath = useCallback(
        (path: string) => history.push(path),
        [history],
    )

    const userDetailPages = useMemo(() => {
        const userAccounts = {
            children: 'Accounts',
            selected: location.pathname.includes(`/${user?.id}/accounts`),
            onClick: () => handlePath(`/masa/user/${user?.id}/accounts`),
        }

        const userDetail = {
            children: 'User detail',
            selected: location.pathname.includes(`/${user?.id}`),
            onClick: () => handlePath(`/masa/user/${user?.id}`),
        }

        return [userDetail, userAccounts]
    }, [location, handlePath, user])

    return (
        <div className='flex flex-col gap-8 p-8 overflow-hidden' style={{}}>
            <SubMenu options={userDetailPages} />
            <div
                className='grid h-full grid-cols-8 grid-rows-3 gap-8 p-8 overflow-hidden'
                style={{}}>
                <Card className='col-span-8 row-span-2 overflow-y-scroll'>
                    <div className='flex flex-row justify-between w-full'>
                        <h1>
                            {firstName} {lastName}
                        </h1>
                        <p className='font-semibold cursor-pointer text-linkYellow'>
                            {role}
                        </p>
                    </div>
                    <div>
                        <p>{email}</p>
                        <p>{phone}</p>
                        <p>
                            <span className='mr-4'>🕐</span>
                            {lastLogin}
                        </p>
                    </div>

                    <div>
                        <p>Balances and Scores</p>
                        <p>
                            <span className='mr-4'>Total</span>
                            {
                                preQualificationStats?.getPreQualificationData
                                    ?.totalAssets
                            }
                        </p>
                        <p>
                            <span className='mr-4'>Total FIAT</span>
                            {
                                preQualificationStats?.getPreQualificationData
                                    ?.totalNetWorth
                            }
                        </p>
                        <p>
                            <span className='mr-4'>Total CRYPTO</span>
                            {
                                preQualificationStats?.getPreQualificationData
                                    ?.totalCryptoWorth
                            }
                        </p>
                        <p>
                            <span className='mr-4'>Credit score</span>
                            Coming soon
                        </p>
                    </div>

                    <div className='flex flex-col items-start mt-8 gap-y-2'>
                        <h3>Notifications</h3>
                        <div className='flex flex-row items-center justify-center gap-x-2'>
                            <Toggle
                                className='toggle'
                                icons={{
                                    checked: null,
                                    unchecked: <ToggleIcon />,
                                }}
                                value={false}
                                onChange={({
                                    target: { checked },
                                }: {
                                    target: { checked: boolean }
                                }) => {}}
                            />
                            <p className='m-0'>SMS</p>
                        </div>
                        <div className='flex flex-row items-center justify-center gap-x-2'>
                            <Toggle
                                className='toggle'
                                icons={{
                                    checked: null,
                                    unchecked: <ToggleIcon />,
                                }}
                                value={false}
                                onChange={({
                                    target: { checked },
                                }: {
                                    target: { checked: boolean }
                                }) => {}}
                            />
                            <p className='m-0'>Email</p>
                        </div>
                    </div>
                </Card>
                <Card className='col-span-8'>
                    <h1>Actions</h1>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 justify-between w-full pt-4 gap-10 text-center'>
                        <div
                            className='flex flex-1 items-center justify-center p-4 rounded-lg cursor-pointer bg-gray5'
                            onClick={() => setOpen(true)}>
                            Refetch transactions data
                        </div>
                        <div
                            className='flex flex-1 items-center justify-center p-4 rounded-lg cursor-pointer bg-gray5'
                            onClick={() =>
                                triggerDeleteUserData(DeleteTypes.SOFT)
                            }>
                            Delete accounts and transactions
                        </div>
                        <div
                            className='flex flex-1 items-center justify-center p-4 rounded-lg cursor-pointer bg-gray5'
                            onClick={() =>
                                triggerDeleteUserData(DeleteTypes.HARD)
                            }>
                            Delete accounts, transactions and connections
                        </div>
                        <div
                            className='flex flex-1 items-center justify-center p-4 transition-transform transform rounded-lg cursor-pointer bg-danger group rotate-y-180-hover'
                            onClick={() =>
                                triggerDeleteUserData(DeleteTypes.TOTAL)
                            }>
                            <h2 className='m-0 text-white group-hover:hidden'>
                                Delete account
                            </h2>
                            <h2 className='hidden m-0 text-white rotate-y-180 group-hover:flex'>
                                Are you sure?
                            </h2>
                        </div>
                    </div>
                </Card>
                <Modal show={open} onClose={() => setOpen(false)}>
                    <div
                        onClick={event => event.stopPropagation()}
                        className={modalStyles}>
                        <DatePicker
                            value={regenerationDates as any}
                            onChange={setRegenerationDates}></DatePicker>
                        <button
                            className={classNames('text-red-600 bg-white')}
                            onClick={_ => setOpen(false)}>
                            {' '}
                            Close{' '}
                        </button>
                        <button
                            className={classNames('text-red-600 bg-white')}
                            onClick={_ => triggerRefetchUserData()}>
                            {' '}
                            Send{' '}
                        </button>
                    </div>
                </Modal>
            </div>
        </div>
    )
}
